import { FC, useContext, memo, MouseEvent } from 'react'
import { useRouter } from 'next/router'
import clsx from 'clsx'

import RecommendationContext from 'context/recommendationContext'
import CardTransitionArea from '../card-transition-area/card-transition-area'
import NewWebinarCard from 'components/cards/new-webinar-card/new-webinar-card'
import NewslineCard from 'components/cards/newsline-card/newsline-card'
import Auth from 'store/auth'
import OriginLink from 'components/parts/originLink/originLink'
import useCardWidth from 'hooks/useCardWidth'
import useWebinarSubscription from 'hooks/useWebinarSubscription'

import { setElementArrActive } from 'utils/formatFutureWebinars'
import { cutHashFromURL } from 'utils/getURLData'
import { Pathname } from 'constants/data'
import { IEventCard } from 'store/data-types'
import { IMaterial } from 'constants/types/webinar.types'
import { popupName } from 'constants/auth/auth-data'
import { originLectures } from 'utils/analytics/library/lectures'
import { originArticle } from 'utils/analytics/library/article'
import { A_LOG_REG_HASH, originRegWebinars } from 'utils/analytics/other/registration/registration'
import {
  A_wf_clickSubscription,
  originSubscribe,
  originFWebinar,
} from 'utils/analytics/webinars/webinar_future'

import styles from './recomendations.module.scss'
import commonMaterialStyle from 'styles/common/common-material.module.scss'

interface IProps {
  isVertical?: boolean
  isVerticalMobile?: boolean
  noFixedWidth?: boolean
  noMargin?: boolean
  noPadding?: boolean
  withOutTitle?: boolean
  eventWrapperModificator?: string
  blockModificator?: string
  cardModificator?: string
  AData?: {
    originSub?: originSubscribe
    originClick?: originFWebinar
    originForArticle?: originLectures | originArticle
  }
}

const Recommendations: FC<IProps> = ({
  isVertical,
  isVerticalMobile,
  noFixedWidth,
  noMargin,
  eventWrapperModificator,
  blockModificator,
  cardModificator,
  noPadding,
  withOutTitle,
  AData,
}: IProps) => {
  const router = useRouter()
  // const [userClickWebinarId, setClickId] = useState(0)
  const { recommendations, setRecommend } = useContext(RecommendationContext)
  const cardWidth = useCardWidth()
  const { unsubscribeWebinar, subscribeWebinar } = useWebinarSubscription()
  const { isAuthorized } = Auth.useAuth()

  const path = cutHashFromURL(router.asPath)

  const handleSubscription = async (e: MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const closest: HTMLElement | null = (e.target as HTMLDivElement).closest('[data-id]')
    try {
      if (closest) {
        const { id, subscription } = closest.dataset

        if (!id) return

        if (!isAuthorized) {
          sessionStorage.setItem(A_LOG_REG_HASH, originRegWebinars.SUBREC)
          return router.push(`${path}#${popupName.registration}`, undefined, { shallow: true })
        }

        if (subscription) await unsubscribeWebinar(id)
        else await subscribeWebinar(id)

        if (AData && AData.originSub) A_wf_clickSubscription(AData.originSub, Number(id))

        const nextWebinarState = setElementArrActive(
          recommendations.cards as IEventCard[],
          Number(id),
          'isSubscribed',
          !subscription
        )
        setRecommend({ ...recommendations, cards: nextWebinarState })
      }
    } catch {
      console.log('Не удается отправить запрос')
    }
  }

  if (!recommendations.cards.length) return <></>

  return (
    <div>
      {/* {withTitle && <h2 className={styles.recomsTitle}>Рекомендации</h2>} */}
      {!withOutTitle && (
        <h2 className={commonMaterialStyle.recommendationsTitle}>Наши лекторы рекомендуют</h2>
      )}
      <CardTransitionArea
        noMargin={noMargin}
        eventWrapperModificator={eventWrapperModificator}
        noPadding={noPadding}
        scrollModificator={styles.scrollMod}
      >
        {[
          <div
            key="recomend"
            className={clsx(styles.recomendationBlock, {
              [styles.recomendationBlockVertical]: isVertical,
              [styles.recomendationBlockVerticalMobile]: isVerticalMobile,
              [blockModificator as string]: blockModificator,
            })}
          >
            {recommendations.cards.map((recomend) => {
              const pathName = (recomend as IMaterial).materialType || Pathname.ARTICLE
              const isWebinar = pathName === Pathname.WEBINAR
              // const origin = isWebinar ? ORIGIN_RECOMMENDATIONS : ORIGIN_RECOMMENDATIONS_ARTICLE

              const href = recommendations.isLoaded
                ? `/${pathName}/${recomend.linkTranslation || recomend.id}`
                : '#'

              return (
                <OriginLink
                  href={href}
                  key={recomend.id}
                  className={clsx(styles.basicCardContainer, {
                    [styles.basicCardContainerVertical]: isVertical,
                    [styles.noClick]: !recommendations.isLoaded,
                    [styles.webinarCardContainer]: isWebinar,
                    [cardModificator as string]: cardModificator,
                  })}
                  data-id={recomend.id}
                  data-subscription={(recomend as IEventCard).isSubscribed || ''}
                  style={isWebinar || noFixedWidth ? {} : cardWidth}
                >
                  {isWebinar ? (
                    <NewWebinarCard
                      {...(recomend as IMaterial)}
                      handleToggle={handleSubscription}
                      isLoaded={recommendations.isLoaded || true}
                      AData={{ originSub: AData?.originSub, originClick: AData?.originClick }}
                      cardModificator={styles.cardMod}
                      btnModificator={clsx(
                        styles.btnWrapper,
                        isVertical ? styles.btnModificator : ''
                      )}
                    />
                  ) : (
                    <NewslineCard
                      {...(recomend as any)}
                      descriptionShort={''}
                      wrapModificator={styles.cardModMaterial}
                      modificator={styles.title}
                      withPreview
                      alternativeDate
                      AData={{ origin: AData?.originForArticle }}
                    />
                  )}
                </OriginLink>
              )
            })}
          </div>,
        ]}
      </CardTransitionArea>
    </div>
  )
}

export default memo(Recommendations)
